/**
 * @file NotesPage.tsx
 * @brief Personal Notes library on the web panel, in the phone's look.
 *
 * @details
 * Mounted in all three shells (`/admin`, `/company`, `/app`). Notes are
 * user-scoped, so the base path only decides where links point.
 *
 * **The same library as the phone (DES, 30-09-2026).** This used to be a
 * table. It is now mobile's `NotesListScreen`:
 * - Sections: Pinned, then Today, Yesterday, This Week, This Month, month
 *   (`noteGrouping.ts`, a port of mobile's).
 * - Rows hang off a timeline rail: a red node for a recording ("Generated"),
 *   grey for a typed note ("Manual").
 * - Title, then date, kind line, meeting link, tags, and a two-line preview.
 * - The list grows as you scroll (20 at a time) instead of paging.
 * - The phone's long-press sheet is a "⋯" menu on each row, also opened by
 *   right-click: Rename, Manage Tags, Pin, Select Multiple, Delete.
 * - Select Multiple swaps the dock for a floating bulk bar.
 * - The bottom dock holds an expanding search and the write button. There is
 *   no "Slide to record": recording stays on the phone.
 *
 * Meeting notes (ADR-030, DES 07-10-2026, the phone's d9397fc / 7f996d8):
 * two pill tabs, "Meeting notes" | "Personal notes", the choice remembered
 * per browser (notesLanguage.ts). Meeting notes lists the viewer's Meeting
 * Notes recordings as note rows (MeetingNotesTab); its dock slide starts the
 * recorder in Notes mode straight away, in the notes language last chosen
 * in Setup, with the recording notice under the slide (GDPR Art 13).
 * Personal notes is the library as before, without the notes that live
 * inside a Notes meeting (the recorder's Take notes lines, 386ae73). The
 * search box and the dates filter the open tab. Without the boardroom (the
 * super admin's shell, or turned off for this user) there are no tabs.
 *
 * Agendas (DES 12-10-2026, the phone's NotesListScreen): notes and agendas
 * are different things. Three tabs, "Meeting notes | Notes | Agendas"
 * ("Personal notes" is now "Notes"). Notes lists kind=note, Agendas
 * kind=agenda (`GET /notes?kind=`); shared-with-me notes show on Notes only.
 * The pen on Agendas writes an agenda, and a row's menu moves a note to
 * Agendas or an agenda back to Notes. Without tabs the list is every note.
 *
 * Search is server-side (title, transcript, tags, insights, the date),
 * debounced 350ms like mobile, and kept in the URL (`?search=`).
 *
 * @references
 * - FRONTEND/src/screens/Notes/NotesListScreen.tsx (the mobile twin)
 *
 * @author DES
 * @date 21-08-2026
 * @updated 30-09-2026 — mobile look and feel (timeline sections, dock, menu)
 */

import { HintBubble, HintIcon } from "@/components/ui/HintPopover";
import { useHintPopover } from "@/components/ui/useHintPopover";
import { EmptyState } from "@/components/feedback/EmptyState";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ExpandingSearch } from "@/components/ui/ExpandingSearch";
import {
  CalendarDays,
  Check,
  Loader2,
  Mic,
  MoreHorizontal,
  Pen,
  PenLine,
  Pin,
  PinOff,
  Sparkles,
  SquareCheck,
  Tag,
  Users,
  Trash2,
  X,
} from "lucide-react";
import { AgendaFlag } from "@/components/icons/AgendaFlag";
import { MeetingNotesPen } from "@/components/icons/MeetingNotesPen";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { cn } from "@/shared/theme/cn";
import { dockFadeBehind } from "@/shared/theme/dockGlass";
import { DateRangePopover } from "@/components/ui/DateRangePopover";
import {
  datePresetDefs,
  rangeLabel,
  type RangePreset,
} from "@/features/employee-boardroom/meetingListLogic";
import { isRTLText } from "@/shared/utils/formatters";
import { toast } from "@/shared/hooks/useToast";
import { ApiClientError } from "@/shared/api/client";
import { useNotesBasePath } from "./useNotesBasePath";
import { Skeleton } from "@/components/ui/Skeleton";
import { useLoadingSkeleton } from "@/shared/hooks/useLoadingSkeleton";
import {
  useDeleteNote,
  useNoteTags,
  useNotesInfinite,
  useSharedWithMeNotes,
  useUpdateNote,
  type SharedNote,
} from "./api";
import { groupNotes } from "./noteGrouping";
import { FormSheetTabs, type FormSheetTab } from "@/components/ui/FormSheetTabs";
import { SwipeButton } from "@/components/ui/SwipeButton";
import { useAuth } from "@/shared/auth/auth-context";
import { useCompanyMeetingDetail, useCompanyMeetingsList } from "@/features/company-meetings/api";
import {
  NOTES_V2_PREVIEW_MEETING_ID,
  previewNotesDoc,
} from "@/features/company-meetings/notesDoc";
import type { MeetingRow } from "@/features/company-meetings/schemas";
import { MeetingNotesTab } from "./MeetingNotesTab";
import { MineTeamSwitch } from "@/features/company-meetings/components/MineTeamSwitch";
import { useMeetingListScope } from "@/features/company-meetings/meetingListScope";
import { useStartMeetingNotes } from "./useStartMeetingNotes";
import {
  transparencyLine,
  useRecordingViewerFlags,
} from "@/features/employee-boardroom/recordingViewer";
import {
  MEETING_NOTES_WORKING,
  filterMeetingNotes,
  onlyNotesMeetings,
  sharedWithMeNotesMeetings,
  withoutNotesMeetingNotes,
} from "./meetingNotesList";
import {
  loadNotesLanguage,
  loadNotesLibraryTab,
  saveNotesLibraryTab,
  useLanguageLabel,
  type NotesLibraryTab,
} from "./notesLanguage";
import { NoteRenameDialog } from "./noteUi";
import { TagsSubmenu } from "./TagsMenu";
import { noteKind } from "./schemas";
import {
  formatWhen,
  noteDockGlass,
  noteKindLine,
  noteIsAgenda,
  noteMeetingLabel,
  notePreview,
} from "./noteFormat";
import type { VoiceNote } from "./schemas";

/** Where a row's node sits: row padding (18) + half the title's first line (28). */
const NODE_CENTER_PX = 32;

/** The library tabs (DES 07-10-2026), the phone's labels and icons.
 *  Meeting notes | Notes | Agendas since 12-10-2026 ("Personal notes" was
 *  renamed "Notes"). */
const LIBRARY_TABS: FormSheetTab<NotesLibraryTab>[] = [
  { id: "meetings", label: "Meeting notes", icon: MeetingNotesPen }, // DES 08-10-2026: app icon, was NotebookPen
  { id: "personal", label: "Notes", icon: PenLine },
  { id: "agendas", label: "Agendas", icon: AgendaFlag },
];

// ----------------------------------------------------------------------
// One row — mobile's `NoteRow`
// ----------------------------------------------------------------------
// ----------------------------------------------------------------------
// A note shared with you — read-only: no menu, no select. Same timeline
// look as your own, a grey node, and who shared it. (DES 30-09-2026)
// ----------------------------------------------------------------------
function SharedNoteRow({
  note,
  first,
  last,
  onOpen,
}: {
  note: SharedNote;
  first: boolean;
  last: boolean;
  onOpen: () => void;
}) {
  const title = note.title?.trim() || "Untitled note";
  const preview = notePreview(note);
  return (
    <div className="relative flex gap-2">
      <div className="relative w-6 shrink-0" aria-hidden>
        {!first ? (
          <span
            className="absolute left-1/2 top-0 w-0.5 -translate-x-1/2 bg-border"
            style={{ height: NODE_CENTER_PX }}
          />
        ) : null}
        {!last ? (
          <span
            className="absolute bottom-0 left-1/2 w-0.5 -translate-x-1/2 bg-border"
            style={{ top: NODE_CENTER_PX }}
          />
        ) : null}
        <span
          className="absolute left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-app-page bg-muted-foreground/50"
          style={{ top: NODE_CENTER_PX }}
        />
      </div>
      <button
        type="button"
        onClick={onOpen}
        className="min-w-0 flex-1 py-[18px] text-left transition-opacity active:opacity-60"
      >
        <span
          dir={isRTLText(title) ? "rtl" : "ltr"}
          className="line-clamp-2 text-xl font-bold leading-7 text-foreground"
        >
          {title}
        </span>
        <span className="mt-1 block text-[15px] font-semibold text-foreground">
          {formatWhen(note.created_at)}
        </span>
        {note.shared_by_name ? (
          <span className="mt-1 flex items-center gap-1.5 text-[15px] text-brand">
            <Users className="h-[15px] w-[15px] shrink-0" strokeWidth={1.5} />
            <span className="truncate">Shared by {note.shared_by_name}</span>
          </span>
        ) : null}
        <span className="mt-1 block text-[15px] text-muted-foreground">{noteKindLine(note)}</span>
        {preview ? (
          <span
            dir={isRTLText(preview) ? "rtl" : "ltr"}
            className="mt-1 line-clamp-2 text-[15px] leading-[22px] text-muted-foreground"
          >
            {preview}
          </span>
        ) : null}
      </button>
    </div>
  );
}

/**
 * Loading placeholder in NoteRow's shape: a date heading, then timeline rows
 * (rail + node, title, date, meta, two preview lines). Web-only skeleton
 * (DES 01-10-2026); the phone stays blank until the fetch.
 */
function NotesListSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div role="status" aria-label="Loading notes" className="mt-2 animate-pulse">
      <Skeleton still className="mb-1 mt-5 h-5 w-24" />
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="relative flex gap-2">
          <div className="relative w-6 shrink-0" aria-hidden>
            {i > 0 ? (
              <span
                className="absolute left-1/2 top-0 w-0.5 -translate-x-1/2 bg-border"
                style={{ height: NODE_CENTER_PX }}
              />
            ) : null}
            {i < rows - 1 ? (
              <span
                className="absolute bottom-0 left-1/2 w-0.5 -translate-x-1/2 bg-border"
                style={{ top: NODE_CENTER_PX }}
              />
            ) : null}
            <Skeleton
             
              still
              className="absolute left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{ top: NODE_CENTER_PX }}
            />
          </div>
          <div className="min-w-0 flex-1 py-[18px] pr-10">
            <Skeleton still className="mt-1 h-5 w-3/4" />
            <Skeleton still className="mt-3 h-4 w-28" />
            <Skeleton still className="mt-2.5 h-4 w-40" />
            <Skeleton still className="mt-2.5 h-4 w-full" />
            <Skeleton still className="mt-2 h-4 w-4/5" />
          </div>
        </div>
      ))}
    </div>
  );
}

function NoteRow({
  note,
  first,
  last,
  selecting,
  selected,
  onOpen,
  onToggleSelect,
  onRename,
  tagsMenu,
  onPin,
  onMoveKind,
  onSelectMultiple,
  onDelete,
}: {
  note: VoiceNote;
  first: boolean;
  last: boolean;
  selecting: boolean;
  selected: boolean;
  onOpen: () => void;
  onToggleSelect: () => void;
  onRename: () => void;
  /** "Tags" as a submenu of ⋯ (`TagsSubmenu`), not a dialog (04-10-2026). */
  tagsMenu: ReactNode;
  onPin: () => void;
  /** Notes <-> Agendas (DES 12-10-2026). Absent without the tabs. */
  onMoveKind?: () => void;
  onSelectMultiple: () => void;
  onDelete: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const typed = note.source === "typed";
  const agenda = noteKind(note) === "agenda";
  const noun = agenda ? "Agenda" : "Note";
  const title = note.title?.trim() || (agenda ? "Untitled agenda" : "Untitled note");
  const preview = notePreview(note);
  const meeting = noteMeetingLabel(note);
  const tags = note.tags ?? [];
  const KindIcon = typed ? PenLine : Sparkles;

  return (
    <div
      className="group relative flex gap-2"
      onContextMenu={(e) => {
        if (selecting) return;
        e.preventDefault();
        setMenuOpen(true);
      }}
    >
      {/* Timeline rail: the line runs through the section and stops at its
          first and last node, as on the phone. */}
      <div className="relative w-6 shrink-0" aria-hidden>
        {!first ? (
          <span
            className="absolute left-1/2 top-0 w-0.5 -translate-x-1/2 bg-border"
            style={{ height: NODE_CENTER_PX }}
          />
        ) : null}
        {!last ? (
          <span
            className="absolute bottom-0 left-1/2 w-0.5 -translate-x-1/2 bg-border"
            style={{ top: NODE_CENTER_PX }}
          />
        ) : null}
        {selecting ? (
          <span
            className={cn(
              "absolute left-1/2 flex h-[22px] w-[22px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2",
              selected
                ? "border-brand bg-brand text-white"
                : "border-muted-foreground/40 bg-secondary",
            )}
            style={{ top: NODE_CENTER_PX }}
          >
            {selected ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : null}
          </span>
        ) : (
          <span
            className={cn(
              "absolute left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-app-page",
              typed ? "bg-muted-foreground/50" : "bg-brand",
            )}
            style={{ top: NODE_CENTER_PX }}
          />
        )}
      </div>

      <button
        type="button"
        onClick={selecting ? onToggleSelect : onOpen}
        className="min-w-0 flex-1 py-[18px] pr-10 text-left transition-opacity active:opacity-60"
      >
        <span className="flex min-w-0 items-start gap-1.5">
          <span
            dir={isRTLText(title) ? "rtl" : "ltr"}
            className="line-clamp-2 text-xl font-bold leading-7 text-foreground"
          >
            {title}
          </span>
          {note.is_pinned ? (
            <Pin className="mt-1.5 h-4 w-4 shrink-0 text-brand" strokeWidth={1.5} />
          ) : null}
        </span>
        <span className="mt-1 block text-[15px] font-semibold text-foreground">
          {formatWhen(note.created_at)}
        </span>
        <span className="mt-1 flex items-center gap-1.5 text-[15px] text-muted-foreground">
          <KindIcon
            className={cn("h-[15px] w-[15px] shrink-0", typed ? "" : "text-brand")}
            strokeWidth={1.5}
          />
          {noteKindLine(note)}
        </span>
        {meeting ? (
          <span className="mt-1 flex min-w-0 items-center gap-1.5 text-[15px] text-brand">
            {/* DES 08-10-2026: app icon for an agenda, was CalendarDays */}
            {noteIsAgenda(note) ? (
              <AgendaFlag className="h-[15px] w-[15px] shrink-0" strokeWidth={1.5} />
            ) : (
              <CalendarDays className="h-[15px] w-[15px] shrink-0" strokeWidth={1.5} />
            )}
            <span className="truncate">{meeting}</span>
          </span>
        ) : null}
        {tags.length ? (
          <span className="mt-1 flex min-w-0 items-center gap-1.5 text-[15px] text-muted-foreground">
            <Tag className="h-[15px] w-[15px] shrink-0" strokeWidth={1.5} />
            <span className="truncate">{tags.join(" · ")}</span>
          </span>
        ) : null}
        {preview ? (
          <span
            dir={isRTLText(preview) ? "rtl" : "ltr"}
            className="mt-1 line-clamp-2 text-[15px] leading-[22px] text-muted-foreground"
          >
            {preview}
          </span>
        ) : null}
      </button>

      {!selecting ? (
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`Options for ${title}`}
              className={cn(
                "absolute right-0 top-4 flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-opacity hover:bg-secondary",
                "opacity-0 focus-visible:opacity-100 group-hover:opacity-100",
                menuOpen && "opacity-100",
              )}
            >
              <MoreHorizontal className="h-5 w-5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onSelect={onRename}>
              <Pen className="mr-2 h-4 w-4" /> Rename {noun}
            </DropdownMenuItem>
            {tagsMenu}
            <DropdownMenuItem onSelect={onPin}>
              {note.is_pinned ? (
                <PinOff className="mr-2 h-4 w-4" />
              ) : (
                <Pin className="mr-2 h-4 w-4" />
              )}
              {note.is_pinned ? `Unpin ${noun}` : `Pin ${noun}`}
            </DropdownMenuItem>
            {/* Notes and agendas are different things (DES 12-10-2026). */}
            {onMoveKind ? (
              <DropdownMenuItem onSelect={onMoveKind}>
                {agenda ? (
                  <PenLine className="mr-2 h-4 w-4" />
                ) : (
                  <AgendaFlag className="mr-2 h-4 w-4" />
                )}
                {agenda ? "Move to Notes" : "Move to Agendas"}
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem onSelect={onSelectMultiple}>
              <SquareCheck className="mr-2 h-4 w-4" /> Select Multiple
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={onDelete}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" /> Delete {noun}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
}

// ----------------------------------------------------------------------
// Page
// ----------------------------------------------------------------------
const DATE_PRESETS = datePresetDefs();

export function NotesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const base = useNotesBasePath() ?? "/app";

  const urlSearch = searchParams.get("search") ?? "";
  const [draft, setDraft] = useState(urlSearch);

  // 350ms like the phone, then into the URL so a reload keeps it.
  useEffect(() => {
    const t = window.setTimeout(() => {
      const next = draft.trim();
      if (next === urlSearch) return;
      const usp = new URLSearchParams(searchParams);
      if (next) usp.set("search", next);
      else usp.delete("search");
      setSearchParams(usp, { replace: true });
    }, 350);
    return () => window.clearTimeout(t);
  }, [draft, urlSearch, searchParams, setSearchParams]);

  // Date range (DES 01-10-2026: "notes per bhi aik range picker zaroor hona
  // chahiye top right per"), in the URL like the search. Filtered on the
  // server, so older notes are not missed.
  const fromParam = searchParams.get("from");
  const toParam = searchParams.get("to");
  const rangePreset = searchParams.get("range");
  const dateActive = Boolean(fromParam && toParam);
  const [calendarOpen, setCalendarOpen] = useState(false);
  // The phone's tips icon by the first heading (DES 05-10-2026).
  const tips = useHintPopover("notes_list_tips");
  const setRange = (next: { range: string | null; from: string | null; to: string | null }) => {
    const usp = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(next)) {
      if (v) usp.set(k, v);
      else usp.delete(k);
    }
    setSearchParams(usp, { replace: true });
  };
  // ----------------------------------------------------------------------
  // Meeting notes (ADR-030, DES 07-10-2026)
  // ----------------------------------------------------------------------
  const { entitlements, user } = useAuth();
  const viewerFlags = useRecordingViewerFlags(user?.id);
  const adminsLine = transparencyLine(viewerFlags.data, "notes");
  const hasMeetings = base !== "/admin" && entitlements.boardroom !== false;
  // The boardroom pages live under /company for a company admin, /app else.
  const boardroomBase = base === "/company" ? "/company" : "/app";
  const [tab, setTab] = useState<NotesLibraryTab>(loadNotesLibraryTab);
  const changeTab = (next: NotesLibraryTab) => {
    setTab(next);
    saveNotesLibraryTab(next);
  };
  const isMeetingsTab = hasMeetings && tab === "meetings";
  // Agendas (DES 12-10-2026). Without the tabs the list is every note.
  const isAgendasTab = hasMeetings && tab === "agendas";
  const listKind = hasMeetings ? (isAgendasTab ? "agenda" : "note") : undefined;
  // Read on every visit: Setup writes it when it starts a Notes meeting.
  const [notesLanguage] = useState(loadNotesLanguage);
  const notesLanguageLabel = useLanguageLabel(notesLanguage);

  // Loaded on both tabs: Personal notes uses the ids to leave out the notes
  // that live inside a Notes meeting. 100 is the server's page cap. Polls
  // while a recording is still being written, so its row settles itself.
  const meetingsList = useCompanyMeetingsList(
    { page: 1, pageSize: 100, tier: "notes" },
    {
      enabled: hasMeetings,
      refetchInterval: (query) =>
        (query.state.data?.meetings ?? []).some((m) => MEETING_NOTES_WORKING.has(m.status))
          ? 5000
          : false,
    },
  );
  // A failure reads as none, never as an error over the user's notes.
  // Filtered here too: an old server ignores `?tier=` (e736654).
  const notesMeetings = useMemo(
    () => (meetingsList.isError ? [] : onlyNotesMeetings(meetingsList.data?.meetings ?? [])),
    [meetingsList.isError, meetingsList.data],
  );
  const notesMeetingIds = useMemo(() => new Set(notesMeetings.map((m) => m.id)), [notesMeetings]);
  // Sharing v2 (DES 21-10-2026): Notes meetings others shared with you come
  // in the default list (`?tier=notes` is your own only). Meeting notes tab
  // only; a failure reads as none.
  // Mine | Team for a company admin (22-10-2026): Team swaps this list for
  // the Notes meetings other members recorded (`?tier=notes` is own only).
  const listScope = useMeetingListScope();
  const teamScope = listScope.scope === "team";
  const sharedMeetingsList = useCompanyMeetingsList(
    { page: 1, pageSize: 100, scope: listScope.scope },
    { enabled: hasMeetings && tab === "meetings" },
  );
  const sharedNotesMeetings = useMemo(() => {
    if (sharedMeetingsList.isError) return [];
    const lo = fromParam ? Date.parse(fromParam) : NaN;
    const hi = toParam ? Date.parse(toParam) : NaN;
    const rows = sharedMeetingsList.data?.meetings ?? [];
    const inRange = (teamScope ? onlyNotesMeetings(rows) : sharedWithMeNotesMeetings(rows)).filter(
      (m) => {
        if (Number.isNaN(lo) || Number.isNaN(hi)) return true;
        const t = Date.parse(m.started_at ?? "");
        return Number.isFinite(t) && t >= lo && t <= hi;
      },
    );
    return filterMeetingNotes(inRange, urlSearch);
  }, [sharedMeetingsList.isError, sharedMeetingsList.data, urlSearch, fromParam, toParam, teamScope]);
  const shownMeetings = useMemo(() => {
    const lo = fromParam ? Date.parse(fromParam) : NaN;
    const hi = toParam ? Date.parse(toParam) : NaN;
    const inRange = notesMeetings.filter((m) => {
      if (Number.isNaN(lo) || Number.isNaN(hi)) return true;
      const t = Date.parse(m.started_at ?? "");
      return Number.isFinite(t) && t >= lo && t <= hi;
    });
    return filterMeetingNotes(inRange, urlSearch);
  }, [notesMeetings, urlSearch, fromParam, toParam]);

  // Dev only (DES 09-10-2026, the phone's NotesListScreen): the Notes v2
  // preview meeting (notesDoc.ts) in the list, opened with the preview flag.
  // Its title is the real meeting's when it loads, else the doc's overview.
  // NOTES_V2_PREVIEW_MEETING_ID is null in a production build.
  const previewMeeting = useCompanyMeetingDetail(
    NOTES_V2_PREVIEW_MEETING_ID,
    hasMeetings && NOTES_V2_PREVIEW_MEETING_ID != null,
  );
  const listedMeetings = useMemo((): MeetingRow[] => {
    const previewId = NOTES_V2_PREVIEW_MEETING_ID;
    const doc = previewNotesDoc();
    if (!previewId || !doc) return shownMeetings;
    const real = previewMeeting.data;
    const row: MeetingRow = {
      ...(real ?? { id: previewId, status: "ready" }),
      id: previewId,
      tier: "notes",
      title: real?.title?.trim() || doc.overview,
      meeting_overview: doc.overview,
    };
    return [row, ...shownMeetings.filter((m) => m.id !== previewId)];
  }, [shownMeetings, previewMeeting.data]);

  // One slide starts a Meeting Notes recording (DES 07-10-2026, the phone's
  // 7f996d8: slide, Setup, slide was a double action). Notes needs nothing
  // else from Setup: no title (the recorder's Take notes can name it), no
  // participants or agenda, the notes language last chosen there, the
  // default microphone. The notice under the slide is the same as Setup's,
  // so the slide carries the same 'setup_slide' confirmation (GDPR Art 13).
  // The checks are Setup's own, so a start that cannot record says why here.
  // Moved to useStartMeetingNotes (20-10-2026) so Home's "Start notes"
  // starts the same way.
  const notesStart = useStartMeetingNotes(boardroomBase);
  const startingNotes = notesStart.starting;
  const startMeetingNotes = () => notesStart.start({ noticeMethod: "setup_slide" });

  const list = useNotesInfinite(urlSearch, 20, { from: fromParam, to: toParam }, listKind);
  // Not the notes that live inside a Notes meeting: those are the lines typed
  // on its recorder, shown in the meeting's "Your notes" (DES 07-10-2026,
  // the phone's 386ae73). The server filters these too once deployed.
  const notes = useMemo(
    () =>
      withoutNotesMeetingNotes(list.data?.pages.flatMap((p) => p.items) ?? [], notesMeetingIds),
    [list.data, notesMeetingIds],
  );
  // Filtered results are remembered apart, so an empty search never hides
  // the skeleton of the plain list.
  const notesSkeleton = useLoadingSkeleton(
    urlSearch || dateActive ? "notes.filtered" : "notes",
    list.isLoading,
    list.data ? notes.length : undefined,
  );
  const total = list.data?.pages[0]?.total ?? 0;
  const sections = useMemo(() => groupNotes(notes), [notes]);

  // Shared with me: one small list, filtered here by the same search box.
  // On the Notes tab only (DES 12-10-2026, as the phone).
  const shared = useSharedWithMeNotes(!isAgendasTab);
  // Searched client-side, so the raw list decides the skeleton.
  const sharedSkeleton = useLoadingSkeleton("notes.shared", shared.isLoading, shared.data?.length);
  const sharedNotes = useMemo(() => {
    const all = shared.data ?? [];
    const q = urlSearch.trim().toLowerCase();
    const lo = fromParam ? Date.parse(fromParam) : NaN;
    const hi = toParam ? Date.parse(toParam) : NaN;
    const inRange = (n: (typeof all)[number]) => {
      if (Number.isNaN(lo) || Number.isNaN(hi)) return true;
      const t = Date.parse((n.shared_at || n.created_at) ?? "");
      return Number.isFinite(t) && t >= lo && t <= hi;
    };
    if (!q) return all.filter(inRange);
    return all.filter(inRange).filter((n) =>
      [n.title, notePreview(n), n.shared_by_name, ...(n.tags ?? [])]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [shared.data, urlSearch, fromParam, toParam]);
  const sharedSections = useMemo(() => groupNotes(sharedNotes), [sharedNotes]);

  // Grow the list as the reader nears the end.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && list.hasNextPage && !list.isFetchingNextPage) {
          void list.fetchNextPage();
        }
      },
      { rootMargin: "600px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [list]);

  const update = useUpdateNote();
  const remove = useDeleteNote();
  const tagSuggestions = useNoteTags();

  const [renaming, setRenaming] = useState<VoiceNote | null>(null);
  const [deleting, setDeleting] = useState<VoiceNote[] | null>(null);
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // The dialog shows the note as it is now, not as it was when opened.

  const fail = (title: string, e: unknown) =>
    toast({
      variant: "destructive",
      title,
      description: e instanceof ApiClientError ? e.message : "Please try again.",
    });

  const exitSelection = () => {
    setSelecting(false);
    setSelected(new Set());
  };

  // Notes <-> Agendas (DES 12-10-2026): the row leaves this tab for the other.
  const moveKind = (note: VoiceNote) => {
    const next = noteKind(note) === "agenda" ? "note" : "agenda";
    update.mutate(
      { id: note.id, kind: next },
      {
        onSuccess: () =>
          toast({ title: next === "agenda" ? "Moved to Agendas" : "Moved to Notes" }),
        onError: (e) => fail("Move failed", e),
      },
    );
  };

  /** "note" / "agenda" for the open tab's words. */
  const noun = isAgendasTab ? "agenda" : "note";

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      // One call per note, as on the phone.
      for (const n of deleting) await remove.mutateAsync(n.id);
      toast({
        title:
          deleting.length === 1
            ? noteKind(deleting[0]) === "agenda"
              ? "Agenda deleted"
              : "Note deleted"
            : `${deleting.length} ${noun}s deleted`,
      });
      setDeleting(null);
      exitSelection();
    } catch (e) {
      fail("Could not delete", e);
    }
  };

  const subtitle = isMeetingsTab
    ? notesMeetings.length === 0
      ? "Meeting notes"
      : `${notesMeetings.length} meeting note${notesMeetings.length === 1 ? "" : "s"}`
    : isAgendasTab
      ? total === 0
        ? urlSearch
          ? "No matches"
          : "Agendas"
        : `${total} agenda${total === 1 ? "" : "s"}`
      : total === 0
        ? urlSearch
          ? "No matches"
          : "Quick capture & transcripts"
        : `${total} note${total === 1 ? "" : "s"}`;

  return (
    // Full width like Home, Boardrooms and the meeting detail (DES 30-09-2026).
    <div className="flex min-h-[calc(100dvh-5rem)] w-full flex-col px-5 pb-6 pt-6 md:px-8">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[28px] font-bold leading-9 tracking-tight">Notes Library</h1>
          <p className="mt-1 text-[15px] text-muted-foreground">{subtitle}</p>
        </div>
        {/* The date filter, top right: the Boardrooms circle and picker. */}
        <span className="relative shrink-0">
          <DateRangePopover
            open={calendarOpen}
            onOpenChange={setCalendarOpen}
            presets={DATE_PRESETS}
            value={{
              presetId: rangePreset,
              from: fromParam ? new Date(fromParam) : null,
              to: toParam ? new Date(toParam) : null,
            }}
            onApply={(v) =>
              setRange({ range: v.presetId, from: v.from.toISOString(), to: v.to.toISOString() })
            }
            onClear={() => setRange({ range: null, from: null, to: null })}
            anchor={
              <button
                type="button"
                onClick={() => setCalendarOpen((o) => !o)}
                aria-label={
                  dateActive && fromParam && toParam
                    ? `Dates: ${rangeLabel((rangePreset as RangePreset | null) ?? "custom", new Date(fromParam), new Date(toParam))}`
                    : "Filter by date"
                }
                title="Filter by date"
                className={cn(
                  "flex h-14 w-14 items-center justify-center rounded-full border transition-colors",
                  dateActive
                    ? "border-brand bg-brand text-white hover:bg-brand/90"
                    : "border-black/5 bg-black/[0.04] text-brand hover:bg-black/[0.08] dark:border-white/[0.07] dark:bg-white/[0.06]",
                )}
              >
                <CalendarDays className="h-6 w-6" strokeWidth={1.5} />
              </button>
            }
          />
          {dateActive ? (
            <button
              type="button"
              onClick={() => setRange({ range: null, from: null, to: null })}
              aria-label="Clear dates"
              className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-app-page bg-app-text text-app-page"
            >
              <X className="h-3.5 w-3.5" strokeWidth={2.5} />
            </button>
          ) : null}
        </span>
      </div>

      {/* Meeting notes | Notes | Agendas (DES 07-10-2026, Agendas 12-10-2026).
          Not while selecting notes, as on the phone. */}
      {hasMeetings && !selecting ? (
        <div className="mt-5">
          <FormSheetTabs<NotesLibraryTab>
            compact
            tone="phone"
            label="Notes library"
            tabs={LIBRARY_TABS}
            value={tab}
            onChange={changeTab}
          />
        </div>
      ) : null}

      {isMeetingsTab ? (
        <div className="min-w-0">
          <MeetingNotesTab
            meetings={listedMeetings}
            notesPreviewId={NOTES_V2_PREVIEW_MEETING_ID}
            loading={meetingsList.isLoading}
            filtered={Boolean(urlSearch) || dateActive}
            boardroomBase={boardroomBase}
            languageLabel={notesLanguageLabel}
            sharedMeetings={teamScope ? [] : sharedNotesMeetings}
            sharedLoading={sharedMeetingsList.isLoading}
            scopeSwitch={<MineTeamSwitch />}
            teamMeetings={teamScope ? sharedNotesMeetings : null}
          />
        </div>
      ) : (
      /* Two columns from lg (DES 30-09-2026): your notes on the left, the
         notes other people shared with you on the right. Stacked on a phone.
         Agendas has no shared column (DES 12-10-2026), but keeps the same
         column width. A hairline down the middle of the gap splits the two
         (DES 21-10-2026), in the page grid's colour. */
      <div className="lg:grid lg:grid-cols-2 lg:gap-12">
        <div className="min-w-0">
          <div className="mt-6 flex items-center justify-between gap-2">
            <h2 className="text-[13px] font-bold uppercase tracking-[0.6px] text-muted-foreground">
              {isAgendasTab ? "My agendas" : "My notes"}
            </h2>
            <HintIcon open={tips.open} onClick={tips.toggle} anchorRef={tips.anchorRef} className="-my-2" />
          </div>
          {tips.open ? (
            <HintBubble
              lines={[
                "Right-click a note (or hover it for ⋯) to rename, tag, pin, select or delete it.",
                "Click a note to read, replay and edit it.",
              ]}
              onClose={tips.close}
              anchorRef={tips.anchorRef}
              className="mb-2 mt-1"
            />
          ) : null}
          {/* Loading: skeleton rows if this list had notes last time, else
              blank, then the notes or the empty state (useLoadingSkeleton). */}
          {list.isLoading ? (
            notesSkeleton ? <NotesListSkeleton /> : null
          ) : list.error ? (
            <div className="mt-10">
              <p className="text-[15px] text-muted-foreground">
                Could not load your {noun}s.
              </p>
              <button
                type="button"
                className="mt-2 text-sm font-semibold text-brand"
                onClick={() => void list.refetch()}
              >
                Try again
              </button>
            </div>
          ) : notes.length === 0 ? (
            <EmptyState
              shape="note"
              animate={!urlSearch && !dateActive}
              title={
                urlSearch
                  ? "No matches"
                  : dateActive
                    ? "Nothing in this period"
                    : isAgendasTab
                      ? "No agendas yet"
                      : "No notes yet"
              }
              description={
                urlSearch
                  ? isAgendasTab
                    ? "No agendas match your search."
                    : "No notes match your search."
                  : dateActive
                    ? `Pick another time period to see more ${noun}s.`
                    : isAgendasTab
                      ? "Write or record what you mean to raise, then attach it to a meeting."
                      : "Write your first note with the pen below, or record one on your phone."
              }
            />
          ) : (
            <div className="mt-2">
              {sections.map((section) => (
                <section key={section.label}>
                  <h2 className="mb-1 mt-4 text-lg font-semibold tracking-[-0.5px] text-foreground/40">
                    {section.label}
                  </h2>
                  {section.notes.map((note, i) => (
                    <NoteRow
                      key={note.id}
                      note={note}
                      first={i === 0}
                      last={i === section.notes.length - 1}
                      selecting={selecting}
                      selected={selected.has(note.id)}
                      onOpen={() => navigate(`${base}/notes/${note.id}`)}
                      onToggleSelect={() =>
                        setSelected((prev) => {
                          const next = new Set(prev);
                          if (next.has(note.id)) next.delete(note.id);
                          else next.add(note.id);
                          return next;
                        })
                      }
                      onRename={() => setRenaming(note)}
                      tagsMenu={
                        <TagsSubmenu
                          tags={note.tags ?? []}
                          suggestions={tagSuggestions.data ?? []}
                          save={(next) => update.mutateAsync({ id: note.id, tags: next })}
                        />
                      }
                      onPin={() =>
                        update.mutate(
                          { id: note.id, isPinned: !note.is_pinned },
                          { onError: (e) => fail("Could not save", e) },
                        )
                      }
                      onMoveKind={hasMeetings ? () => moveKind(note) : undefined}
                      onSelectMultiple={() => {
                        setSelecting(true);
                        setSelected(new Set([note.id]));
                      }}
                      onDelete={() => setDeleting([note])}
                    />
                  ))}
                </section>
              ))}
              <div ref={sentinelRef} className="h-1" />
              {list.isFetchingNextPage ? (
<div className="flex justify-center py-4" role="status" aria-label="Loading more">
                  <Loader2 className="h-5 w-5 animate-spin text-brand" aria-hidden />
                </div>
              ) : null}
            </div>
          )}
        </div>

        {isAgendasTab ? null : (
        <aside className="relative mt-10 min-w-0 lg:mt-0 lg:before:absolute lg:before:-left-6 lg:before:bottom-0 lg:before:top-6 lg:before:w-px lg:before:bg-border/60">
          <h2 className="mt-6 text-[13px] font-bold uppercase tracking-[0.6px] text-muted-foreground">
            Shared with me
          </h2>
          {shared.isLoading ? (
            sharedSkeleton ? <NotesListSkeleton rows={2} /> : null
          ) : sharedNotes.length === 0 ? (
            <p className="mt-6 text-[15px] leading-[22px] text-muted-foreground">
              {urlSearch
                ? "No shared note matches your search."
                : "Nothing shared with you yet. When a colleague shares a note, it appears here."}
            </p>
          ) : (
            <div className="mt-2">
              {sharedSections.map((section) => (
                <section key={section.label}>
                  <h2 className="mb-1 mt-4 text-lg font-semibold tracking-[-0.5px] text-foreground/40">
                    {section.label}
                  </h2>
                  {section.notes.map((note, i) => (
                    <SharedNoteRow
                      key={note.id}
                      note={note as SharedNote}
                      first={i === 0}
                      last={i === section.notes.length - 1}
                      onOpen={() => navigate(`${base}/notes/${note.id}`)}
                    />
                  ))}
                </section>
              ))}
            </div>
          )}
        </aside>
        )}
      </div>
      )}

      {/* ---------------- Dock: search + write, or the bulk bar ---------------- */}
      {/* THE dock fade behind it (DES 08-10-2026). */}
      <div className={cn("pointer-events-none sticky bottom-6 z-20 mt-auto flex justify-center pt-8", dockFadeBehind)}>
        {selecting ? (
          <div
            className={cn(
              "pointer-events-auto flex h-14 items-center gap-4 px-3",
              "rounded-[32px] border border-border bg-card shadow-[0_8px_30px_rgba(0,0,0,0.12)]",
            )}
          >
            <button
              type="button"
              aria-label="Cancel selection"
              onClick={exitSelection}
              className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-secondary"
            >
              <X className="h-5 w-5" />
            </button>
            <span className="min-w-[6rem] text-center text-base font-bold">
              {selected.size} selected
            </span>
            <button
              type="button"
              aria-label="Delete selected"
              disabled={selected.size === 0}
              onClick={() => setDeleting(notes.filter((n) => selected.has(n.id)))}
              className="flex h-10 w-10 items-center justify-center rounded-full text-destructive hover:bg-secondary disabled:opacity-40"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
        ) : isMeetingsTab ? (
          /* Meeting notes: the search, then the slide that starts a Meeting
             Notes recording (no Write button: a typed note is Personal).
             The recording notice sits under the slide, Setup's words, on the
             page colour so the list never runs through them (GDPR Art 13). */
          <div className="pointer-events-auto flex w-full max-w-[34rem] flex-col items-center gap-2 rounded-[28px] bg-app-page px-1 pb-1 pt-1">
            <div className="flex w-full items-center gap-3">
              <ExpandingSearch
                value={draft}
                onChange={setDraft}
                placeholder="Search title or summary"
                label="Search meeting notes"
                openWidth="w-[min(14rem,50vw)]"
              />
              <div className="min-w-0 flex-1">
                <SwipeButton
                  text="Slide to start"
                  icon={Mic}
                  loading={startingNotes}
                  disabled={startingNotes}
                  onSwipeComplete={() => void startMeetingNotes()}
                  accessibilityLabel="Start meeting notes"
                  accessibilityHint="Slide or press Enter to start recording a meeting in Meeting Notes mode"
                />
              </div>
            </div>
            <p className="px-4 text-center text-[13px] leading-[18px] text-muted-foreground">
              By sliding to start, you confirm everyone here knows this meeting is recorded and
              transcribed.
            </p>
            {/* Sharing v2 (DES 21-10-2026, decision 5): company members only. */}
            {adminsLine ? (
              <p className="px-4 text-center text-[13px] leading-[18px] text-muted-foreground">
                {adminsLine}
              </p>
            ) : null}
          </div>
        ) : (
          <div className="pointer-events-auto flex items-center gap-3">
            {/* The shared page search, born here (DES 01-10-2026). */}
            <ExpandingSearch
              value={draft}
              onChange={setDraft}
              placeholder={isAgendasTab ? "Search agendas" : "Search title, tags, transcript, or date"}
              label={isAgendasTab ? "Search agendas" : "Search notes"}
            />
            {/* On Agendas the pen writes an agenda (DES 12-10-2026). */}
            <Link
              to={isAgendasTab ? `${base}/notes/new?kind=agenda` : `${base}/notes/new`}
              aria-label={isAgendasTab ? "Write an agenda" : "Write a note"}
              title={isAgendasTab ? "Write an agenda" : "Write a note"}
              className={cn(noteDockGlass, "flex h-14 w-14 items-center justify-center")}
            >
              <PenLine className="h-6 w-6 text-brand" strokeWidth={1.5} />
            </Link>
          </div>
        )}
      </div>

      <NoteRenameDialog
        open={Boolean(renaming)}
        onOpenChange={(o) => !o && setRenaming(null)}
        initialTitle={renaming?.title ?? ""}
        saving={update.isPending}
        onSave={(title) =>
          renaming &&
          update.mutate(
            { id: renaming.id, title },
            {
              onSuccess: () => setRenaming(null),
              onError: (e) => fail("Could not rename", e),
            },
          )
        }
      />

      <AlertDialog open={Boolean(deleting)} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleting && deleting.length > 1
                ? `Delete ${deleting.length} ${noun}s?`
                : deleting?.[0] && noteKind(deleting[0]) === "agenda"
                  ? "Delete agenda?"
                  : "Delete note?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Notes and their audio are removed for good. Any meeting a note is attached to keeps
              its own intelligence; only the link goes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={remove.isPending}
              onClick={(e) => {
                e.preventDefault();
                void confirmDelete();
              }}
            >
              {deleting && deleting.length > 1 ? "Delete all" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default NotesPage;
